import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Card, CardBody, Row, Col, CardHeader, Collapse } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faServer,
  faExternalLinkAlt,
  faChevronDown,
  faChevronUp,
} from "@fortawesome/free-solid-svg-icons";
import { useJobDetails } from "../../../../providers/jobDetailsProvider";
import { utils } from "../../../../utils/utils";
import InformationModal from "../../../InformationModal";
import { programmingApi } from "../../../../services/programmingServices";

const PAGE_NUMBER = 0;
const PAGE_SIZE = 99999;

const ProgrammingSummary = () => {
  const [jobDetails] = useJobDetails();
  const { job } = jobDetails;
  const [informationModal, setInformationModal] = useState(
    utils.initInformationModal
  );
  const [programming, setProgramming] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(true);

  useEffect(() => {
    const fetchProgramming = async () => {
      if (job) {
        try {
          const result = await programmingApi.getAllProgramming({
            jobId: job.id,
            page: PAGE_NUMBER,
            pageSize: PAGE_SIZE,
          });
          if (result.data && result.data.length > 0) {
            setProgramming(result.data[result.data.length - 1]);
          }
        } catch (err) {
          setInformationModal({
            isOpen: true,
            title: "Error",
            body: "There was an error fetching the programming data.",
            onClose: () =>
              setInformationModal({ ...utils.initInformationModal }),
          });
        }
      }
    };

    fetchProgramming();
  }, [job]);

  const { programmerName, checkerName, status } = useMemo(() => {
    const formatUserName = (user) =>
      user?.firstName
        ? `${user.firstName} ${user.lastName ?? ""}`.trim()
        : "Not Assigned";

    return {
      programmerName: formatUserName(programming?.programmer),
      checkerName: formatUserName(programming?.checker),
      status: programming?.programmingStatus?.description ?? "-",
    };
  }, [programming]);

  if (!job) {
    return null;
  }

  return (
    <Card className="m-0 p-3">
      <CardHeader className="bg-white border-0 p-0">
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            <div className="rounded-circle d-flex align-items-center justify-content-center p-2 bg-soft-blue">
              <FontAwesomeIcon icon={faServer} size="lg" />
            </div>
            <h4 className="mb-0 ml-3 font-weight-bold">Programming</h4>
          </div>
          <div className="d-flex align-items-center">
            <Link
              to="/programming"
              className="text-dark"
              state={{
                tab: utils.programmingTabs[programming?.programmingStatus?.id],
              }}
            >
              <FontAwesomeIcon icon={faExternalLinkAlt} size="lg" />
            </Link>
            <button
              type="button"
              className="btn btn-link text-dark p-0 ml-3"
              onClick={() => setIsCollapsed((prev) => !prev)}
              aria-label={
                isCollapsed ? "Expand programming" : "Collapse programming"
              }
            >
              <FontAwesomeIcon
                icon={isCollapsed ? faChevronDown : faChevronUp}
                size="lg"
              />
            </button>
          </div>
        </div>
      </CardHeader>
      <Collapse isOpen={!isCollapsed}>
        <CardBody className="p-0 pt-3">
          <Row>
            <Col>
              <h4 className="font-weight-bold m-0 mb-1">{status}</h4>
            </Col>
          </Row>
          <Row className="mt-3">
            <Col>
              <p className="text-muted mb-0">Programmer</p>
              <h6 className="m-0">{programmerName}</h6>
            </Col>
            <Col>
              <p className="text-muted mb-0">Checker</p>
              <h6 className="m-0">{checkerName}</h6>
            </Col>
          </Row>
        </CardBody>
      </Collapse>
      {informationModal.isOpen && (
        <InformationModal
          title={informationModal.title}
          body={informationModal.body}
          onClose={informationModal.onClose}
        />
      )}
    </Card>
  );
};

export default ProgrammingSummary;
